/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useCallback } from "react";
import { Box, Text, Title } from '@/shared/ui';
import { useNavigate } from 'react-router';
import { useMediaQuery } from "@/shared/hooks/use-media-query";
import { MobileNav } from "@/shared/components/MobileNav";
import { cn } from '@/shared/ui/utils/tailwind';
import Footer from "@pages/unauthenticated/landing/footer";
import { bodyClassTail } from "./PolicyPageLayout.styles";

const containerClass = cn(
  'py-(--space-12) px-[clamp(1.5rem,5vw,6rem)] bg-(--surface-primary) w-full box-border overflow-x-hidden',
  'max-md:py-(--space-6) max-md:px-(--space-4)',
);

const headerClass = 'mb-(--space-8) pb-(--space-4) border-b border-(--border-subtle)';

const titleClass = cn(
  'text-h1 font-(--font-bold) text-(--text-primary) m-0 mb-(--space-2) leading-snug tracking-(--tracking-tight)',
  'max-md:text-h3',
);

const metadataClass = 'flex gap-(--space-4) flex-wrap';

const metadataItemClass = 'text-body-sm text-(--text-secondary) m-0 italic';

const bodyClass = cn(
  'text-body leading-loose text-(--text-primary) break-words overflow-x-hidden',
  'max-md:text-body-sm max-md:leading-loose',

  // Hide first h2, its following p, and the hr after that
  '[&>div>h2:first-child]:hidden',
  '[&>div>h2:first-child+p]:hidden',
  '[&>div>h2:first-child+p+hr]:hidden',

  // First h3 (or h3 right after the hidden h2+p+hr block) gets no top margin
  '[&>div>h3:first-child]:mt-0',
  '[&>div>h2:first-child+p+hr+h3]:mt-0',

  // h3
  '[&_h3]:text-h3 [&_h3]:font-(--font-semibold) [&_h3]:text-(--text-primary)',
  '[&_h3]:mt-(--space-10) [&_h3]:mb-(--space-3) [&_h3]:ml-0 [&_h3]:mr-0',
  '[&_h3]:pb-(--space-2) [&_h3]:border-b [&_h3]:border-(--border-subtle)',
  '[&_h3]:leading-snug [&_h3]:tracking-(--tracking-normal)',
  'max-md:[&_h3]:text-h5 max-md:[&_h3]:mt-(--space-8)',

  // h4
  '[&_h4]:text-body-lg [&_h4]:font-(--font-semibold) [&_h4]:text-(--text-primary)',
  '[&_h4]:mt-(--space-7) [&_h4]:mb-(--space-2) [&_h4]:ml-0 [&_h4]:mr-0',
  '[&_h4]:leading-normal',
  'max-md:[&_h4]:text-body-lg max-md:[&_h4]:mt-(--space-5)',

  // h5
  '[&_h5]:text-body [&_h5]:font-(--font-semibold) [&_h5]:text-(--text-primary)',
  '[&_h5]:mt-(--space-5) [&_h5]:mb-(--space-1-5) [&_h5]:ml-0 [&_h5]:mr-0',
  '[&_h5]:leading-normal',

  // h6
  '[&_h6]:text-body-sm [&_h6]:font-(--font-semibold) [&_h6]:text-(--text-secondary)',
  '[&_h6]:mt-(--space-4) [&_h6]:mb-(--space-1) [&_h6]:ml-0 [&_h6]:mr-0',
  '[&_h6]:leading-normal [&_h6]:uppercase [&_h6]:tracking-(--tracking-wide)',

  // p
  '[&_p]:mt-0 [&_p]:mb-(--space-3-5) [&_p]:ml-0 [&_p]:mr-0',

  // ul, ol
  '[&_ul]:pl-(--space-6) [&_ol]:pl-(--space-6)',
  '[&_ul]:mt-(--space-1) [&_ul]:mb-(--space-4) [&_ul]:ml-0 [&_ul]:mr-0',
  '[&_ol]:mt-(--space-1) [&_ol]:mb-(--space-4) [&_ol]:ml-0 [&_ol]:mr-0',

  // li
  '[&_li]:mb-(--space-1-5) [&_li]:leading-loose',
  '[&_li>p]:mb-(--space-1)',

  // ul style types
  '[&_ul]:list-disc [&_ul_ul]:list-[circle] [&_ul_ul]:mt-(--space-1)',

  // ol style types
  '[&_ol]:list-decimal [&_ol_ol]:list-[lower-alpha] [&_ol_ol]:mt-(--space-1)',

  // hr
  '[&_hr]:border-none [&_hr]:border-t [&_hr]:border-(--border-subtle) [&_hr]:my-(--space-8)',
  'max-md:[&_hr]:my-(--space-6)',

  ...bodyClassTail,
);

interface PolicyPageLayoutProps {
  title: string;
  lastUpdated?: string;
  version?: string;
  lastUpdatedLabel?: string;
  versionLabel?: string;
  children: React.ReactNode;
}

const PolicyPageLayout: React.FC<PolicyPageLayoutProps> = ({
  title,
  lastUpdated,
  version,
  lastUpdatedLabel = "Last updated",
  versionLabel = "Version",
  children,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();

  const handleScrollTo = useCallback((sectionId: string) => {
    navigate(`/#${sectionId}`);
  }, [navigate]);

  return (
    <>
      <Box className={containerClass}>
        <div className="max-w-full w-full">
          <div className={headerClass}>
            <Title order={1} className={titleClass}>{title}</Title>
            
            {(lastUpdated || version) && (
              <div className={metadataClass}>
                {lastUpdated && (
                  <Text component="p" className={metadataItemClass}>
                    <strong>{lastUpdatedLabel}:</strong> {lastUpdated}
                  </Text>
                )}
                {version && (
                  <Text component="p" className={metadataItemClass}>
                    <strong>{versionLabel}:</strong> {version}
                  </Text>
                )}
              </div>
            )}
          </div>
          
          <Title order={2} className="sr-only text-2xl font-semibold">{title}</Title>
          <div className={cn(bodyClass, "max-w-prose")}>
            {children}
          </div>
        </div>
      </Box>

      <Footer />

      {isMobile && (
        <MobileNav
          onScrollTo={handleScrollTo}
        />
      )}
    </>
  );
};

export default PolicyPageLayout;
